import {registerStyle, styleRule, UI, StyleSheet} from "../../../../stem-core/src/ui/UI";

import {authService} from "../../../../client/connection/Services";
import {AuthHelper} from "../../../AuthHelper";
import {LinkButton} from "../../../../core/ui/LinkButton.jsx";
import {Messages} from "../../../Messages";
import {Router} from "../../../../stem-core/src/ui/Router";
import {CONFIRMATION_CODE_TYPES, LOGIN_URL} from "../../../Constants";
import {ShortCodeEmailConfirmationForm} from "../components/ShortCodeEmailConfirmationForm";
import {LongCodeEmailConfirmationForm} from "../components/LongCodeEmailConfirmationForm";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../../blink-sdk/utils/AnalyticsClient";
import {authFormService} from "../../../services/AuthFormService";
import {MessageElement} from "../../../widget/ui/MessageElement";
import {wrapInSpinner} from "../../../../core/ui/LoadingSpinner.jsx";
import {Toast} from "../../../ui/Toast.jsx";


class LoginWithEmailCodeStyle extends StyleSheet {
    @styleRule
    container = {
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL,
    };

    @styleRule
    message = {
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR,
        marginBottom: 14,
    };

    @styleRule
    text = {
        display: "inline",
    };

    @styleRule
    resendCodeButton = {
        display: "flex",
        justifyContent: "space-around",
        width: "100%",
        paddingTop: 6,
    };
}

@registerStyle(LoginWithEmailCodeStyle)
export class LoginWithEmailCode extends UI.Element {
    canDisplayResendCodeOption = true;
    confirmed = false;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            onWrongEmailButtonClick: () => Router.changeURL(LOGIN_URL), // TODO @auth @cleanup this won't be necessary once we merge for TNR
        }
    }

    getBeforeInput() {
        const {styleSheet, confirmed} = this;
        const messageFormatter = (authFormService.confirmationCodeType === CONFIRMATION_CODE_TYPES.SHORT) ?
            Messages.enterConfirmationCodeShort : Messages.enterConfirmationCodeLong;

        return [
            <MessageElement className={styleSheet.message} message={messageFormatter(authFormService.registrationMail)}/>,
            <LinkButton
                disabled={confirmed}
                onClick={() => this.handleWrongEmailLinkClick()}
                label={Messages.wrongEmail}
            />
        ];
    }

    getEmailConfirmationFormClass() {
        return authFormService.confirmationCodeType === CONFIRMATION_CODE_TYPES.LONG
            ? LongCodeEmailConfirmationForm : ShortCodeEmailConfirmationForm;
    }

    render() {
        const {styleSheet} = this;
        const EmailConfirmationFormClass = this.getEmailConfirmationFormClass();

        return [
            <div className={styleSheet.message}>
                {this.getBeforeInput()}
            </div>,
            <EmailConfirmationFormClass ref="emailConfirmationForm"
                                        onCodeAutomaticallyVerified={() => {
                                            this.canDisplayResendCodeOption = false;
                                            this.redraw();
                                        }}
                                        onSubmit={(code) => this.handleSubmit(code)}/>,
            this.getAfterInput(),
        ];
    }

    getAfterInput() {
        const {confirmed, canDisplayResendCodeOption, styleSheet} = this;

        if (!canDisplayResendCodeOption) {
            return null;
        }

        return <LinkButton
            className={styleSheet.resendCodeButton}
            disabled={!confirmed}
            label={Messages.resendCode}
            onClick={() => this.resendCode()}
        />
    }

    handleWrongEmailLinkClick() {
        if (this.emailConfirmationForm) {
            this.options.onWrongEmailButtonClick();
        }
    }

    @wrapInSpinner
    async handleSubmit(code) {
        try {
            dispatchAnalyticsEvent(AnalyticsEventType.ATTEMPT_CONFIRM_EMAIL_CODE);
            await this.confirmEmailCode(code);
            this.onConfirmSuccess();
        } finally {
            this.confirmed = true;
            this.redraw(); // TODO @cleanup handle this
        }
    }

    onConfirmSuccess() {
        if (this.options.onSuccess) {
            this.options.onSuccess();
        }
        AuthHelper.confirmedAuthenticationAction();
    }

    async confirmEmailCode(code) {
        await AuthHelper.loginWithEmailCode(code);

        dispatchAnalyticsEvent(AnalyticsEventType.USER_LOGIN, {registration: authFormService.isRegistration});
    }

    async resendCode() {
        try {
            await AuthHelper.requestLoginCode();
            Toast.showSuccess(Messages.confirmationCodeResent);
        } catch (error) {
            Toast.showError(error, Messages.unexpectedError);
        }
    }

    onMount() {
        super.onMount();
        this.attachUpdateListener(authFormService, () => this.redraw());
        authService.executeWhenAuthenticated(() => {
            /*
            If the user completed email confirmation in another tab, redirect / close the current page.
            The timeout is needed because by confirming the code through this component the expected behaviour is not
            instant, and it is a bit hard to check whether or not the confirmation was made through this component
            otherwise.
             */
            setTimeout(() => {
                if (!this.confirmed) {
                    AuthHelper.confirmedAuthenticationAction();
                }
            }, 300);
        });
    }
}
